// Shared responsive-prop helper for Cadence utility components.
//
// See the RFC "Responsive utility components vs. media-query hooks":
// https://reverb.atlassian.net/wiki/spaces/DS/pages/4168319031
//
// A responsive prop accepts either a plain value (applied at the base/desktop
// tier) or a partial breakpoint object. The utilities are desktop-first:
// `desktop` is the unprefixed base and `tablet`/`mobile` are `max-width`
// overrides, so an omitted breakpoint inherits from the next-larger one via the
// CSS cascade. Prefer these typed props over `useMediaQuery` for styling.

export type Breakpoint = 'desktop' | 'tablet' | 'mobile';

/** A plain value (base/desktop) or a partial per-breakpoint object. */
export type Responsive<T> = T | Partial<Record<Breakpoint, T>>;

// Utility-class prefix per breakpoint; `desktop` is the unprefixed base.
const BREAKPOINT_PREFIXES: Record<Breakpoint, string> = {
  desktop: '',
  tablet: 'tablet-',
  mobile: 'mobile-',
};

const BREAKPOINTS = Object.keys(BREAKPOINT_PREFIXES) as Breakpoint[];

// Own-property check (ignores the prototype chain), so only breakpoint keys
// explicitly set on the object are ever considered — never inherited ones.
const hasOwn = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key);

function isBreakpointObject<T>(
  value: Responsive<T>,
): value is Partial<Record<Breakpoint, T>> {
  // Require an actual (own) breakpoint key so an object-shaped scalar `T` isn't
  // mistaken for a breakpoint map, and an inherited key can't trigger it.
  return typeof value === 'object' && value !== null && BREAKPOINTS.some((bp) => hasOwn(value, bp));
}

/**
 * Ensures a partial responsive object carries the component's base default at
 * `desktop` — the `{ ...defaults, ...override }` merge pattern used elsewhere
 * (e.g. RCModalActionGroup), applied to the base tier.
 *
 * Needed only for props whose default differs from the CSS initial value — e.g.
 * RCFlex `display` (flex vs block) and `gap` (2 vs 0) — so that a `{ mobile: … }`
 * object doesn't silently reset the base to the CSS initial. A scalar value, or an
 * object that already sets `desktop`, is returned unchanged.
 */
export function withBaseDefault<T>(value: Responsive<T>, base: T): Responsive<T> {
  if (!isBreakpointObject(value)) return value;
  // Spread copies own props only (dropping anything inherited); set `desktop` last
  // with a nullish fallback so an absent or explicit-`undefined` desktop (common in
  // conditionally-built objects) falls back to `base` rather than being dropped.
  const own = { ...value };
  return { ...own, desktop: own.desktop ?? base };
}

/**
 * Maps a responsive prop to a list of utility class names.
 *
 * `toClass(value, prefix)` builds the class for a single breakpoint value —
 * `prefix` is `''` for desktop/base and `'tablet-'` / `'mobile-'` otherwise.
 * Return a falsy value to omit the class (e.g. a default that needs no class at
 * the base tier).
 */
export function responsiveClasses<T>(
  value: Responsive<T> | undefined,
  toClass: (value: T, prefix: string) => string | false | null | undefined,
): string[] {
  if (value === undefined) return [];

  const entries: Array<[T, string]> = isBreakpointObject(value)
    ? BREAKPOINTS
      .filter((bp) => hasOwn(value, bp) && value[bp] !== undefined)
      .map((bp) => [value[bp] as T, BREAKPOINT_PREFIXES[bp]])
    : [[value, BREAKPOINT_PREFIXES.desktop]];

  return entries
    .map(([v, prefix]) => toClass(v, prefix))
    .filter((cls): cls is string => Boolean(cls));
}
